import {
  CircleAlert,
  CirclePause,
  CirclePlay,
  Download,
  Eye,
  Info,
  Mail,
  MessageSquare,
  Pencil,
  SendHorizontal,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type {
  Campaign,
  CampaignRecipient,
  CampaignRecipientFilter,
  CampaignRecipientStatus,
  CampaignResendSegment,
  CampaignStats,
  CampaignSummarySource,
} from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { RefreshButton } from '../../components/RefreshButton';
import { identityLine } from '../../lib/channelMeta';
import { htmlToText } from '../../lib/htmlText';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { useInfiniteScroll } from './useInfiniteScroll';
import {
  AUDIENCE_LABELS,
  campaignChannelChip,
  campaignWhen,
  recipientAddress,
} from './campaignTools';
import { CampaignFollowUpPanel } from './CampaignFollowUpPanel';
import { CampaignReviewPanel } from './CampaignReviewPanel';
import { CampaignBookingsCard } from './CampaignBookings';
import { CampaignActivity, CampaignKpis, CampaignOutcome, CampaignReach } from './CampaignOverview';
import { CampaignSummaryCard } from './CampaignSummaryCard';
import { CampaignWebsiteCard } from './CampaignWebsite';
import { useComms } from './context';
import { EmailFrame } from './EmailBody';
import { RichText, type RichTextValue } from './RichText';
import { TableRowsSkeleton } from './Skeletons';
import { useCampaignBookings } from './useCampaignBookings';
import { useModuleRefresh } from './useModuleRefresh';

const RECIPIENT_TONES: Record<
  CampaignRecipientStatus,
  { bg: string; fg: string; label: string }
> = {
  pending: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: 'Pending' },
  // In flight right now — reads as activity, not as a resting state.
  processing: { bg: 'var(--c-primary-muted)', fg: 'var(--c-primary)', label: 'Sending' },
  sent: { bg: 'var(--c-green-muted)', fg: 'var(--c-green)', label: 'Sent' },
  failed: { bg: 'var(--c-red-muted)', fg: 'var(--c-red)', label: 'Failed' },
  bounced: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)', label: 'Bounced' },
  // Not a failure and not a bad number — somebody who asked not to be texted.
  // Muted rather than red, because nothing here went wrong.
  suppressed: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: 'Opted out' },
  // Left out by the frequency cap — the operator's own rule, not a fault.
  capped: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: 'Recently contacted' },
  // Left out because the address bounced before — the operator's choice, made
  // on the pre-flight review, so muted like the other skips.
  bounced_before: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: 'Bounced before' },
};

const STATUS_CHIP: Record<string, { bg: string; fg: string; label: string }> = {
  draft: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: 'Draft' },
  in_review: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)', label: 'Waiting for review' },
  sending: { bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)', label: 'Sending' },
  stopped: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)', label: 'Stopped' },
  completed: { bg: 'var(--c-green-muted)', fg: 'var(--c-green)', label: 'Sent' },
  failed: { bg: 'var(--c-red-muted)', fg: 'var(--c-red)', label: 'Failed' },
};

/**
 * One campaign's outcome, on two tabs: Overview (the AI summary once it has
 * finished, the figures, what it did and who it never reached, hour by hour,
 * the bookings, links and website — CampaignOverview.tsx) and Recipients (the
 * per-recipient table) — plus the mail itself, and from all of it a way to
 * follow it up.
 *
 * The body is here because this screen is the delivery record and, until it was,
 * the record did not include what was actually said. It is also the only place a
 * reviewer can read a submitted campaign: `in_review` is frozen, so its author
 * cannot open it in the builder for them.
 */
/**
 * What `GET /campaigns/:id/recipients` returns per page. Named here because the
 * table has no total to compare against: a page this size is the only evidence
 * there may be another, and a short one is the end of the list.
 */
const RECIPIENTS_PER_PAGE = 50;

/**
 * The recipient list's Show menu — one grouped select where there used to be
 * eleven chips, grouped by the question an operator brings to the list: did
 * they get it, did they do anything, or why not.
 *
 * The two channels are different lists, not one with renamed rows. `Opened`
 * is not a recipient status: the server answers it with the predicate the
 * `opened` follow-up group uses, so ticking those rows picks exactly who that
 * follow-up would mail. A text has no open, and `Delivered` — a carrier
 * confirmation, a stronger signal than an open rather than a rename of it —
 * takes its place.
 */
type ShowOption = { key: CampaignRecipientFilter | 'all'; label: string; count: (s: CampaignStats) => number };
const showGroups = (isSms: boolean): { label: string; options: ShowOption[] }[] => [
  { label: 'Everyone', options: [{ key: 'all', label: 'All recipients', count: (s) => s.total }] },
  {
    label: 'Got it',
    options: isSms
      ? [
          { key: 'sent', label: 'Sent', count: (s) => s.sent },
          { key: 'delivered', label: 'Delivered', count: (s) => s.delivered },
          { key: 'undelivered', label: 'Not confirmed', count: (s) => Math.max(0, s.sent - s.delivered) },
        ]
      : [
          { key: 'sent', label: 'Sent', count: (s) => s.sent },
          { key: 'opened', label: 'Opened', count: (s) => s.opened },
        ],
  },
  {
    label: 'Did something',
    options: [
      { key: 'clicked', label: isSms ? 'Tapped a link' : 'Clicked', count: (s) => s.clicked ?? 0 },
      { key: 'replied', label: 'Replied', count: (s) => s.replied ?? 0 },
      { key: 'unsubscribed', label: isSms ? 'Opted out here' : 'Unsubscribed', count: (s) => s.unsubscribed },
    ],
  },
  {
    label: 'Didn’t get it',
    options: [
      { key: 'pending', label: 'Still to send', count: (s) => s.pending },
      { key: 'bounced', label: isSms ? 'Bad number' : 'Bounced', count: (s) => s.bounced },
      { key: 'failed', label: 'Failed', count: (s) => s.failed },
      { key: 'suppressed', label: 'Already opted out', count: (s) => s.suppressed },
      { key: 'capped', label: 'Recently contacted', count: (s) => s.capped ?? 0 },
      { key: 'bounced_before', label: isSms ? 'Bad number before' : 'Bounced before', count: (s) => s.bouncedBefore ?? 0 },
    ],
  },
];

/** Which card on the Overview a summary point's figures come from. */
const SOURCE_CARDS: Partial<Record<CampaignSummarySource, string>> = {
  funnel: 'outcome',
  timing: 'activity',
  links: 'links',
  bounces: 'reach',
  bookings: 'bookings',
};

/** Pages 2+ of one campaign-and-filter's recipients — see [CampaignResults]. */
interface AppendedPages {
  /** `<campaignId>:<statusFilter>` these rows were fetched for. */
  key: string;
  rows: CampaignRecipient[];
  /** The highest page LANDED. Page one is `useLoad`'s and is not counted here. */
  page: number;
  /** Was the last page that landed full? A short one is the end of the list. */
  lastPageFull: boolean;
  loading: boolean;
}

const noPages = (key: string): AppendedPages => ({
  key,
  rows: [],
  page: 1,
  lastPageFull: false,
  loading: false,
});

export function CampaignResults({
  campaignId,
  onResendDraft,
  onOpenAnalytics,
}: {
  campaignId: number;
  /** Where a freshly created resend draft goes — the builder, to be sent. */
  onResendDraft: (draft: Campaign) => void;
  /** The Analytics Campaigns card, where this mount can reach it. */
  onOpenAnalytics?: () => void;
}) {
  const { listTime, messageTime, zone, formats } = useTimeFormat();
  const { source, inboxes, capabilities } = useComms();
  const [statusFilter, setStatusFilter] = useState<CampaignRecipientFilter | 'all'>('all');
  const [bodyOpen, setBodyOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  /** The group a next step asked the Follow up panel to open on. */
  const [resendSegment, setResendSegment] = useState<CampaignResendSegment | undefined>(undefined);
  /**
   * Overview (how it went) or Recipients (who, one by one). Two tabs because
   * the list is a different kind of reading — looking for a name or a run of
   * bounces — and below eight cards of figures it was never found.
   */
  const [tab, setTab] = useState<'overview' | 'recipients'>('overview');
  const [bookingDays, setBookingDays] = useState(7);
  // Ticked rows, by email, kept across pages and filter changes — the whole
  // point is picking a few bounces off one page and a few failures off another.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /**
   * The recipient table as a spreadsheet. The server answers inside the
   * envelope like every other read, so the file is made here — a Blob and a
   * throwaway link, nothing that needs a second auth path.
   */
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { filename, csv } = await source.api.exportCampaignRecipients(campaignId);
      // A BOM so Excel reads the file as UTF-8 and names with accents survive.
      const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setControlError(e instanceof ApiError ? e.message : 'Could not export the recipients.');
    } finally {
      setExporting(false);
    }
  };
  /** The brake: confirming it, running it, and whatever it or Resume said. */
  const [confirmStop, setConfirmStop] = useState(false);
  const [controlBusy, setControlBusy] = useState<'stop' | 'resume' | 'save' | 'send' | 'return' | null>(
    null,
  );
  const [controlError, setControlError] = useState<string | null>(null);
  /**
   * The copy of a stopped campaign, while it is being rewritten. Null is "not
   * editing" — seeded from the campaign when the editor opens rather than held in
   * step with it, so a five-second poll landing mid-sentence cannot overwrite what
   * is being typed.
   */
  const [editing, setEditing] = useState<{ subject: string; body: RichTextValue } | null>(null);

  const campaignLoad = useLoad(() => source.api.getCampaign(campaignId), [source, campaignId]);
  /**
   * Page one only. The rest are APPENDED as the table is scrolled — a recipient
   * list is read looking for a name or a run of bounces, and a pager made that
   * a matter of remembering which page you were on. Held apart from `useLoad`
   * rather than folded into it because the load owns page one and must be free
   * to replace it: a five-second poll on a sending campaign runs through here.
   */
  const recipientsLoad = useLoad(
    () =>
      source.api.listCampaignRecipients(campaignId, {
        page: 1,
        ...(statusFilter === 'all' ? {} : { status: statusFilter }),
      }),
    [source, campaignId, statusFilter],
  );
  /**
   * The pages appended UNDER page one, and which list they belong to.
   *
   * One keyed object rather than four pieces of state: every one of them is
   * only meaningful for the campaign and filter it was fetched for, and a
   * response that lands after either moved is dropped by comparing the key
   * inside the updater — where the current one is known for certain, which no
   * captured variable can promise.
   */
  const listKey = `${campaignId}:${statusFilter}`;
  const [appendedState, setAppendedState] = useState<AppendedPages>(() => noPages(listKey));
  // Adjusted during render, the way React documents resetting state from a
  // changed input: an effect would paint the previous campaign's tail under the
  // new one's first page for a frame.
  if (appendedState.key !== listKey) setAppendedState(noPages(listKey));
  const appended = appendedState.key === listKey ? appendedState : noPages(listKey);
  /**
   * Held across the render that has not happened yet. `appended.loading` is the
   * flag the UI reads, but it is a render behind the click — two observer
   * callbacks in one frame would both see false and fetch the same page twice,
   * and appending it twice is duplicate rows in front of the agent.
   */
  const inFlight = useRef(false);

  const loadMoreRecipients = useCallback(() => {
    if (inFlight.current) return;
    const key = appended.key;
    const page = appended.page + 1;
    inFlight.current = true;
    setAppendedState((current) => (current.key === key ? { ...current, loading: true } : current));
    source.api
      .listCampaignRecipients(campaignId, {
        page,
        ...(statusFilter === 'all' ? {} : { status: statusFilter }),
      })
      .then(
        (rows) =>
          setAppendedState((current) =>
            current.key !== key
              ? current
              : {
                  ...current,
                  page,
                  rows: [...current.rows, ...rows],
                  lastPageFull: rows.length >= RECIPIENTS_PER_PAGE,
                  loading: false,
                },
          ),
        () =>
          // Silent, and the page is NOT recorded, so the next time the bottom
          // comes into view it is asked for again. This is a list growing under
          // someone's thumb rather than an action they took, and an error note
          // for it would be noise on a table already showing what it has.
          setAppendedState((current) =>
            current.key !== key ? current : { ...current, loading: false },
          ),
      )
      .finally(() => {
        inFlight.current = false;
      });
  }, [appended.key, appended.page, source, campaignId, statusFilter]);

  const campaign = campaignLoad.data;
  const campaignStatus = campaign?.status;
  const reloadCampaign = campaignLoad.reload;
  const reloadRecipients = recipientsLoad.reload;
  const bookingsLoad = useCampaignBookings(campaign, bookingDays);
  const reloadBookings = bookingsLoad.reload;
  useModuleRefresh(() => {
    reloadCampaign();
    reloadRecipients();
    reloadBookings();
  });

  // A sending campaign keeps moving — follow it without manual refreshes.
  useEffect(() => {
    if (campaignStatus !== 'sending') return;
    const timer = setInterval(() => {
      reloadCampaign();
      reloadRecipients();
    }, 5000);
    return () => clearInterval(timer);
  }, [campaignStatus, reloadCampaign, reloadRecipients]);

  const firstPage = recipientsLoad.data ?? [];
  const recipients = appended.rows.length ? [...firstPage, ...appended.rows] : firstPage;
  /**
   * A short page is the end of the list — the endpoint reports no total, so a
   * full page is the only evidence there might be another after it.
   */
  const hasMoreRecipients =
    appended.page > 1 ? appended.lastPageFull : firstPage.length >= RECIPIENTS_PER_PAGE;
  const moreSentinel = useInfiniteScroll({
    hasMore: hasMoreRecipients,
    // The poll on a sending campaign replaces page one every five seconds;
    // appending into the middle of that would add rows it is about to drop.
    busy: appended.loading || recipientsLoad.stale,
    onLoadMore: loadMoreRecipients,
  });

  // Only when there is nothing to draw. While a send is followed this re-reads
  // every five seconds, and one failed poll used to replace the funnel, the
  // recipients and the Stop button with a line of text until the next one.
  if (campaignLoad.error && !campaign) {
    return <div className="comms-error-note">{campaignLoad.error.message}</div>;
  }
  if (!campaign) {
    return <div className="comms-spin">Loading campaign…</div>;
  }

  const stats = campaign.stats ?? {
    total: 0,
    pending: 0,
    sent: 0,
    failed: 0,
    bounced: 0,
    opened: 0,
    delivered: 0,
    suppressed: 0,
    unsubscribed: 0,
  };
  const isSms = campaign.channel === 'sms';

  // Null capabilities means "not loaded yet" and is treated permissively
  // everywhere in this module: the server refuses either way, and a hidden
  // control on a slow /meta reads as a missing feature.
  const campaignAccess = capabilities?.access.features.campaigns;
  const mayResend =
    campaignAccess === undefined || campaignAccess === 'draft' || campaignAccess === 'full';
  /**
   * Stopping, resuming and rewriting a stopped campaign all sit one rung higher,
   * at `campaigns: full` — the level that sent it in the first place.
   */
  const mayControl = campaignAccess === undefined || campaignAccess === 'full';
  const runControl = (
    which: 'stop' | 'resume' | 'send' | 'return',
    call: () => Promise<unknown>,
    fallback: string,
  ) => {
    setControlBusy(which);
    setControlError(null);
    call().then(
      () => {
        setControlBusy(null);
        setConfirmStop(false);
        // Both answers change the campaign AND the recipient rows under it, and
        // the poll only runs while a campaign is sending — so a stop has to
        // fetch its own last picture of what went out.
        reloadCampaign();
        reloadRecipients();
      },
      (e: unknown) => {
        setControlBusy(null);
        setConfirmStop(false);
        setControlError(e instanceof ApiError ? e.message : fallback);
      },
    );
  };
  // Unsaved edits and Resume are mutually exclusive: resuming would mail the half
  // that is still waiting with the copy on the server, not the copy on screen.
  const dirty =
    editing !== null &&
    (editing.subject !== (campaign.subject ?? '') || editing.body.html !== (campaign.bodyHtml ?? ''));
  const saveCopy = () => {
    if (!editing) return;
    setControlBusy('save');
    setControlError(null);
    source.api
      .updateCampaignCopy(campaign.id, {
        subject: editing.subject,
        bodyHtml: editing.body.html,
        bodyText: editing.body.text.trim() ? editing.body.text : null,
      })
      .then(
        () => {
          setControlBusy(null);
          setEditing(null);
          reloadCampaign();
        },
        (e: unknown) => {
          setControlBusy(null);
          setControlError(e instanceof ApiError ? e.message : 'Could not save the wording.');
        },
      );
  };
  const chip = STATUS_CHIP[campaign.status] ?? STATUS_CHIP.draft;
  const channelChip = campaignChannelChip(campaign.channel);
  const runningShare = campaignWhen(campaign, zone, formats).replace(/^(Running|Stopped) · /, '');
  const inbox = inboxes.find((candidate) => candidate.id === campaign.inboxId);
  // Only a run that is over can be resent — a campaign still going out is not
  // "already sent", and one that never left has nothing to resend to.
  const finished = campaign.status === 'completed' || campaign.status === 'failed';
  const resendBlocker = !finished
    ? campaign.status === 'sending'
      ? 'This campaign is still going out'
      : campaign.status === 'stopped'
        ? 'This campaign is stopped part way through — resume it first'
        : 'This campaign has not been sent yet'
    : stats.total === 0
      ? 'This campaign resolved to nobody'
      : null;
  // Ticking rows is only ever a way into a resend, so it appears exactly when
  // one is possible.
  const canPick = mayResend && resendBlocker === null;
  // Whichever column addresses this campaign — see `recipientAddress`. Ticking
  // rows has to select the same people the resend will then mail.
  const pageEmails = recipients.map((row) => recipientAddress(row));
  const allPagePicked = pageEmails.length > 0 && pageEmails.every((email) => picked.has(email));
  const togglePicked = (emails: string[], on: boolean) =>
    setPicked((current) => {
      const next = new Set(current);
      for (const email of emails) {
        if (on) next.add(email);
        else next.delete(email);
      }
      return next;
    });

  /** "View" on a Didn't reach row, a next step, a summary point: the list, narrowed. */
  const showRecipients = (filter: CampaignRecipientFilter) => {
    setStatusFilter(filter);
    setTab('recipients');
  };
  const followUp = (segment?: CampaignResendSegment) => {
    setResendSegment(segment);
    setResendOpen(true);
  };
  /**
   * A summary point's link: the card its figures came from, scrolled to and
   * outlined for a moment so the eye lands on it. Replies are the list rather
   * than a card — the replies ARE people.
   */
  const showSource = (source: CampaignSummarySource) => {
    if (source === 'replies') {
      showRecipients('replied');
      return;
    }
    const card = SOURCE_CARDS[source];
    const node = card ? document.querySelector<HTMLElement>(`[data-card="${card}"]`) : null;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    node.animate?.(
      [{ boxShadow: '0 0 0 3px var(--c-primary-muted)' }, { boxShadow: '0 0 0 0 transparent' }],
      { duration: 1600, easing: 'ease-out' },
    );
  };
  const started = Boolean(campaign.startedAt);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="cmg-results-head">
        <div>
          <div className="cmg-results-title">{campaign.name}</div>
          <div className="cmg-results-sub">
            {AUDIENCE_LABELS[campaign.audienceType]}
            {inbox ? ` · from ${identityLine(inbox)}` : ''} ·{' '}
            {campaign.startedAt ? `started ${listTime(campaign.startedAt)}` : 'not started'}
            {campaign.completedAt ? ` · finished ${listTime(campaign.completedAt)}` : ''}
          </div>
          {/* What the old At a glance panel held that is not a result: how
              it was paced, and what it followed up. */}
          <div className="cmg-results-sub">
            Sent at up to {campaign.throttlePerMinute} a minute
            {campaign.resendOfCampaignId !== null
              ? ` · a follow-up to campaign #${campaign.resendOfCampaignId}`
              : ''}
          </div>
        </div>
        {/* What it went out AS, beside what became of it. Two chips rather than
            one line because they answer different questions and change at
            different times: the channel is fixed for the life of the campaign,
            the status moves while you are looking at it. It is also what says
            which of two different things the funnel below is measuring. */}
        <span
          className="cmg-results-status"
          style={{ background: channelChip.bg, color: channelChip.fg }}
        >
          {channelChip.label}
        </span>
        <span className="cmg-results-status" style={{ background: chip.bg, color: chip.fg }}>
          {chip.label}
          {campaign.status === 'sending' ? ` · ${runningShare}` : ''}
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="cbtn"
          title={isSms ? 'The text this campaign sent' : 'The email this campaign sent'}
          onClick={() => setBodyOpen((open) => !open)}
        >
          {isSms ? <MessageSquare size={13} /> : <Mail size={13} />}
          {bodyOpen
            ? isSms
              ? 'Hide the message'
              : 'Hide the email'
            : isSms
              ? 'View the message'
              : 'View the email'}
        </button>
        {campaign.status === 'sending' && mayControl && (
          <button
            type="button"
            className="cbtn"
            disabled={controlBusy !== null}
            title="Halt this campaign — the people already mailed keep their mail"
            onClick={() => setConfirmStop(true)}
          >
            {controlBusy === 'stop' ? <span className="btn-spin" /> : <CirclePause size={13} />}
            {controlBusy === 'stop' ? 'Stopping…' : 'Stop sending'}
          </button>
        )}
        {mayResend && (
          <button
            type="button"
            className="cbtn cbtn-primary"
            disabled={resendBlocker !== null}
            title={resendBlocker ?? 'Send it again — to everyone, or to the people who did or did not act on it'}
            onClick={() => followUp(undefined)}
          >
            <SendHorizontal size={13} />
            {picked.size > 0 ? `Follow up with ${picked.size.toLocaleString()} picked` : 'Follow up'}
          </button>
        )}
        <RefreshButton
          className="cbtn"
          size={13}
          refreshing={campaignLoad.refreshing || recipientsLoad.refreshing}
          busy={campaignLoad.loading || recipientsLoad.loading}
          onClick={() => {
            campaignLoad.reload();
            recipientsLoad.reload();
            bookingsLoad.reload();
          }}
        />
      </div>

      {campaign.error && (
        <div className="comms-error-note">
          <CircleAlert size={15} />
          {campaign.error}
        </div>
      )}

      {controlError && <div className="comms-error-note">{controlError}</div>}

      {/* A stopped campaign is the one state on this screen that is waiting on a
          decision, so it sits above the funnel with everything that decision
          needs: how far it got, the wording (which is usually WHY it was stopped),
          a second opinion on that wording, and the way back on.

          Only the copy is editable. The half already mailed went out to a resolved
          audience, from this mailbox, at this pace — change any of those and the
          rest of the run finishes as a different campaign, with nothing recording
          which recipient got which. The server refuses them by name. */}
      {/* The reviewer's two answers to a submission. A submitted campaign is
          uneditable and undeletable by its author, and the builder never opens
          it — so without these it sat "In review" for good, with nobody able to
          approve it, bin it, or hand it back. */}
      {campaign.status === 'in_review' && (
        <div className="cmg-card cmg-review" style={{ gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="cmg-card-title">Waiting for review</div>
              <div className="cmg-results-sub">
                Nothing has been mailed yet.{' '}
                {mayControl
                  ? 'Send it as it stands, or return it to its author — a returned draft can be scheduled, or sent on credits, from the builder.'
                  : 'Somebody with full campaign access can send it or return it to you.'}
              </div>
            </div>
            {mayControl && (
              <>
                <button
                  type="button"
                  className="cbtn"
                  disabled={controlBusy !== null}
                  title="Hand it back to its author as a draft"
                  onClick={() =>
                    runControl(
                      'return',
                      () => source.api.returnCampaign(campaign.id),
                      'Could not return the campaign.',
                    )
                  }
                >
                  {controlBusy === 'return' && <span className="btn-spin" />}
                  Return to draft
                </button>
                <button
                  type="button"
                  className="cbtn cbtn-primary"
                  disabled={controlBusy !== null}
                  title="Send it now, from the tenant's own mailbox at its usual pace"
                  onClick={() =>
                    runControl(
                      'send',
                      () => source.api.sendCampaign(campaign.id),
                      'Could not send the campaign.',
                    )
                  }
                >
                  {controlBusy === 'send' && <span className="btn-spin" />}
                  Send now
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {campaign.status === 'stopped' && (
        // `cmg-stopped` carries no styles — it is the stable hook the handbook's
        // screenshot capture aims at, the way the settings shots aim at
        // `data-section` rather than at a label that could be reworded.
        <div className="cmg-card cmg-stopped" style={{ gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="cmg-card-title">Stopped part way through</div>
              <div className="cmg-results-sub">
                {stats.sent.toLocaleString()} of {stats.total.toLocaleString()} were mailed
                {campaign.stoppedAt ? ` · stopped ${listTime(campaign.stoppedAt)}` : ''}.{' '}
                {stats.pending > 0
                  ? `${stats.pending.toLocaleString()} are still waiting — resuming picks up from there, in this same campaign.`
                  : 'Nobody is left waiting, so resuming simply closes it off.'}
              </div>
            </div>
            {mayControl && (
              <>
                {editing ? (
                  <>
                    <button
                      type="button"
                      className="cbtn"
                      disabled={controlBusy !== null}
                      onClick={() => setEditing(null)}
                    >
                      Discard changes
                    </button>
                    <button
                      type="button"
                      className="cbtn"
                      disabled={controlBusy !== null || !dirty}
                      title={dirty ? 'Save the wording' : 'Nothing has changed yet'}
                      onClick={saveCopy}
                    >
                      {controlBusy === 'save' && <span className="btn-spin" />}
                      {controlBusy === 'save' ? 'Saving…' : 'Save wording'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="cbtn"
                    title="Change the subject and body before the rest goes out"
                    onClick={() =>
                      setEditing({
                        subject: campaign.subject ?? '',
                        body: {
                          html: campaign.bodyHtml ?? '',
                          text: campaign.bodyText ?? htmlToText(campaign.bodyHtml ?? ''),
                        },
                      })
                    }
                  >
                    <Pencil size={13} />
                    Edit the wording
                  </button>
                )}
                <button
                  type="button"
                  className="cbtn cbtn-primary"
                  disabled={controlBusy !== null || dirty}
                  title={
                    dirty
                      ? 'Save or discard your changes first — resuming mails the saved wording'
                      : 'Send the rest of this campaign'
                  }
                  onClick={() =>
                    runControl(
                      'resume',
                      () => source.api.resumeCampaign(campaign.id),
                      'Could not resume the campaign.',
                    )
                  }
                >
                  {controlBusy === 'resume' ? (
                    <span className="btn-spin" />
                  ) : (
                    <CirclePlay size={13} />
                  )}
                  {controlBusy === 'resume' ? 'Resuming…' : 'Resume sending'}
                </button>
              </>
            )}
          </div>

          {editing && (
            <>
              <div className="cmg-field">
                <span className="cmg-field-label">Subject</span>
                <input
                  className="cmg-input"
                  value={editing.subject}
                  onChange={(event) =>
                    setEditing((current) =>
                      current ? { ...current, subject: event.target.value } : current,
                    )
                  }
                />
              </div>
              <div className="cmg-field">
                <span className="cmg-field-label">Body</span>
                <RichText
                  value={editing.body}
                  onChange={(body) =>
                    setEditing((current) => (current ? { ...current, body } : current))
                  }
                  placeholder="Hi {{name}} —"
                />
              </div>
              <div className="cmg-hint">
                Merge tags still work: {'{{name}}'} and your company data points are filled in per
                recipient at send time. Only the recipients still waiting see this — the
                {' '}{stats.sent.toLocaleString()} already mailed have the old wording.
              </div>
            </>
          )}

          {mayControl && (
            <CampaignReviewPanel
              channel={campaign.channel}
              // The stored copy while the editor is closed, the live one while it is
              // open — so the verdict always describes the words on screen, and the
              // panel's own staleness rule clears it the moment they change again.
              // A text campaign has no editor here, so it always reads the stored
              // body — which is still the question being asked, because resuming
              // puts that wording in front of everyone who has not had it yet.
              subject={editing ? editing.subject : (campaign.subject ?? '')}
              bodyHtml={editing ? editing.body.html : (campaign.bodyHtml ?? '')}
              bodyText={editing ? editing.body.text : (campaign.bodyText ?? undefined)}
              bodySms={campaign.bodySms ?? ''}
              audienceType={campaign.audienceType}
              fromName={campaign.fromName ?? undefined}
              intro="Worth a read before the rest of the list gets it."
            />
          )}
          {!mayControl && (
            <div className="cmg-hint">
              Resuming or rewriting a stopped campaign needs Campaigns at <b>Send</b> — ask whoever
              sent it.
            </div>
          )}
        </div>
      )}

      {bodyOpen && isSms && (
        <div className="cmg-sent-mail">
          <div className="cmg-preview-mail-head">
            <span>{inbox ? identityLine(inbox) : 'This number'}</span>
            <span className="cmg-preview-subject">
              {campaign.smsBody
                ? `${campaign.smsBody.segments} segment${campaign.smsBody.segments === 1 ? '' : 's'}${
                    campaign.smsBody.encoding === 'UCS-2' ? ' · Unicode' : ''
                  }`
                : ''}
            </span>
          </div>
          {/* No frame and no sandbox: a text is text. Whitespace is preserved
              because the line breaks an operator typed are the whole layout. */}
          <div className="cmg-sent-mail-body">
            <div style={{ padding: 14, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }}>
              {campaign.bodySms || '(empty)'}
            </div>
          </div>
          <div className="cmg-sent-mail-foot">
            Stored exactly as it was written, so merge tags read as {'{{name}}'} — each recipient
            got their own values, and their own segment count with it.
          </div>
        </div>
      )}

      {bodyOpen && !isSms && (
        <div className="cmg-sent-mail">
          <div className="cmg-preview-mail-head">
            <span>
              {campaign.fromName || 'No sender name'}
              {inbox ? ` · ${identityLine(inbox)}` : ''}
            </span>
            <span className="cmg-preview-subject">{campaign.subject || '(no subject)'}</span>
          </div>
          {/* Sandboxed, like every other mail body in the console. Campaign html
              is the one email path the server does NOT sanitize, so the null
              origin is the whole of the containment here.

              Padded from the OUTSIDE rather than by the frame's own document,
              which is shared with the thread: there a bubble is drawn tight
              around the mail, here the mail is a sheet inside a card and its
              first line was landing on the border. */}
          <div className="cmg-sent-mail-body">
            <EmailFrame
              html={campaign.bodyHtml ?? ''}
              title={`${campaign.name} — the email that was sent`}
            />
          </div>
          <div className="cmg-sent-mail-foot">
            Stored exactly as it was written, so merge tags read as {'{{name}}'} — each recipient got
            their own values. Company details are filled in fresh at send time, so a detail changed
            since this went out shows today's, not the one that was mailed.
          </div>
        </div>
      )}

      <div className="cmg-results-tabs" role="tablist" aria-label="Campaign results">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'overview'}
          className={`cmg-results-tab${tab === 'overview' ? ' on' : ''}`}
          onClick={() => setTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'recipients'}
          className={`cmg-results-tab${tab === 'recipients' ? ' on' : ''}`}
          onClick={() => setTab('recipients')}
        >
          Recipients <span>{stats.total.toLocaleString()}</span>
        </button>
      </div>

      {tab === 'overview' && (
        <>
          {/* Only for a finished campaign; nothing at all before then. */}
          <CampaignSummaryCard
            campaign={campaign}
            mayFollowUp={mayResend && resendBlocker === null}
            onFollowUp={followUp}
            onRecipients={showRecipients}
            onSource={showSource}
          />

          {!started ? (
            <div className="cmg-card">
              <div className="cmg-results-sub">
                Nothing has been sent yet, so there is nothing to report. The figures appear here
                as soon as it starts going out.
              </div>
            </div>
          ) : (
            <>
              <CampaignKpis campaign={campaign} stats={stats} bookings={bookingsLoad} days={bookingDays} />

              <div className="cmg-overview-pair" data-card="what-it-did">
                <CampaignOutcome
                  campaign={campaign}
                  stats={stats}
                  bookings={bookingsLoad.data}
                  mayFollowUp={mayResend && resendBlocker === null}
                  onRecipients={showRecipients}
                  onFollowUp={followUp}
                />
                <CampaignReach campaign={campaign} stats={stats} onRecipients={showRecipients} />
              </div>

              <CampaignActivity campaign={campaign} />

              {/* The wider answer than the website card: every booking the
                  people it reached made afterwards, however they made it. */}
              <CampaignBookingsCard
                campaign={campaign}
                days={bookingDays}
                onDays={setBookingDays}
                load={bookingsLoad}
              />

              <div className="cmg-overview-pair">
          {campaign.trackClicks && (campaign.links?.length ?? 0) > 0 && (
            <div className="cmg-card" data-card="links" style={{ gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{isSms ? 'Taps by link' : 'Clicks by link'}</div>
              {campaign.links?.map((link) => (
                <div key={link.id} className="cmg-funnel-row cmg-link-row">
                  <span
                    className="cmg-funnel-label"
                    style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={link.url}
                  >
                    {link.url.replace(/^https?:\/\//, '')}
                  </span>
                  <span className="cmg-funnel-value">
                    {link.people.toLocaleString()} {link.people === 1 ? 'person' : 'people'}
                  </span>
                  <span className="cmg-funnel-pct">
                    {link.clicks.toLocaleString()} {isSms ? 'taps' : 'clicks'}
                  </span>
                </div>
              ))}
              {campaign.links?.some((link) => link.scannerClicks > 0) && (
                <div className="cmg-insight">
                  <Info size={15} />
                  <span>
                    {campaign.links
                      .reduce((sum, link) => sum + link.scannerClicks, 0)
                      .toLocaleString()}
                    {isSms
                      ? ' more look like a phone or carrier fetching the link to draw a preview, and are left out of these numbers.'
                      : ' more opened by mail-security scanners (they check every link before delivery) are left out of these numbers.'}
                  </span>
                </div>
              )}
            </div>
          )}
                <CampaignWebsiteCard campaign={campaign} onOpenAnalytics={onOpenAnalytics} />
              </div>
            </>
          )}
        </>
      )}

      {tab === 'recipients' && (
        <>
          <div className="calls-filterbar">
            <label className="cmg-show">
              <span>Show</span>
              <select
                className="cmg-select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as CampaignRecipientFilter | 'all')}
              >
                {showGroups(isSms).map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options
                      // A tracked-clicks row on a campaign that tracked none would
                      // answer zero for a question it never asked.
                      .filter((option) => option.key !== 'clicked' || campaign.trackClicks)
                      .map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label} · {option.count(stats).toLocaleString()}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </label>
            {picked.size > 0 && (
              <>
                <span className="cmg-picked-count">{picked.size.toLocaleString()} ticked</span>
                <button type="button" className="cbtn" onClick={() => setPicked(new Set())}>
                  Clear
                </button>
              </>
            )}
            <div style={{ flex: 1 }} />
            {/* What used to be Previous/Next. The count replaces them because the
                question the pager was answering — how far into this am I — is the
                only thing lost by scrolling instead of paging. */}
            <span className="cmg-picked-count" style={{ color: 'var(--c-muted)' }}>
              {recipients.length.toLocaleString()} shown
            </span>
            <button type="button" className="cbtn" disabled={exporting} onClick={() => void exportCsv()}>
              <Download size={14} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>

          <div className="ctable">
            <div className="ctable-head">
              {canPick && (
                <div className="cmg-pick-cell">
                  {/* Disabled while the page is being replaced: until the new rows
                      land this box still describes the OLD ones, so a press would
                      tick or untick fifty addresses the operator can no longer see.
                      Same `stale` the rows below already hold their shape on. */}
                  <input
                    type="checkbox"
                    aria-label="Tick every recipient on this page"
                    title="Tick every recipient on this page"
                    disabled={recipientsLoad.stale}
                    checked={allPagePicked && !recipientsLoad.stale}
                    onChange={(event) => togglePicked(pageEmails, event.target.checked)}
                  />
                </div>
              )}
              <div style={{ flex: 1.2 }}>RECIPIENT</div>
              <div style={{ flex: 1.4 }}>{isSms ? 'NUMBER' : 'EMAIL'}</div>
              <div style={{ width: 120, flex: '0 0 120px' }}>STATUS</div>
              <div style={{ width: 120, flex: '0 0 120px' }}>SENT</div>
              <div style={{ width: 110, flex: '0 0 110px' }}>{isSms ? 'DELIVERED' : 'OPENED'}</div>
              {/* One trailing column for "what else is true about this row". An
                  unsubscribe and an error never coexist — a row that failed was
                  never delivered for anyone to press a link in. */}
              <div style={{ flex: 1 }}>NOTES</div>
            </div>
            {/* Paging and the status filter both swap the whole page of rows —
                hold the table's shape rather than showing the previous page. */}
            {recipientsLoad.stale && <TableRowsSkeleton columns={[1.2, 1.4, 1, 1, 1, 1]} count={8} />}
            {!recipientsLoad.stale &&
              recipients.map((recipient, index) => {
                const tone = RECIPIENT_TONES[recipient.status];
                return (
                  <div
                    key={recipient.id}
                    className={`ctable-row${index % 2 === 1 ? ' odd' : ''}`}
                    style={{ minHeight: 44 }}
                  >
                    {canPick && (
                      <div className="cmg-pick-cell">
                        <input
                          type="checkbox"
                          aria-label={`Tick ${recipientAddress(recipient)}`}
                          checked={picked.has(recipientAddress(recipient))}
                          onChange={(event) =>
                            togglePicked([recipientAddress(recipient)], event.target.checked)
                          }
                        />
                      </div>
                    )}
                    <div className="ctable-cell-main" style={{ flex: 1.2 }}>
                      {recipient.name ?? '—'}
                    </div>
                    <div className="ctable-cell-muted" style={{ flex: 1.4 }}>
                      {recipientAddress(recipient)}
                      {/* Only when it cost more than one, because that is the only
                          time the number is news — and it is per RECIPIENT, since
                          their own name is inside the message. */}
                      {isSms && (recipient.segments ?? 1) > 1 ? (
                        <span style={{ color: 'var(--c-muted)' }}>
                          {' '}
                          · {recipient.segments} segments
                        </span>
                      ) : null}
                    </div>
                    <div style={{ width: 120, flex: '0 0 120px' }}>
                      <span className="cchip" style={{ background: tone.bg, color: tone.fg }}>
                        {tone.label}
                      </span>
                    </div>
                    <div className="ctable-cell-muted" style={{ width: 120, flex: '0 0 120px' }}>
                      {recipient.sentAt ? messageTime(recipient.sentAt) : '—'}
                    </div>
                    <div className="ctable-cell-muted" style={{ width: 110, flex: '0 0 110px' }}>
                      {/* The eye is an OPEN and belongs to email only. A carrier
                          receipt is a different fact and gets a plain timestamp —
                          borrowing the icon would quietly claim somebody read it. */}
                      {isSms ? (
                        recipient.deliveredAt ? (
                          messageTime(recipient.deliveredAt)
                        ) : (
                          '—'
                        )
                      ) : recipient.openedAt ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Eye size={11} />
                          {messageTime(recipient.openedAt)}
                          {recipient.openCount > 1 ? ` ×${recipient.openCount}` : ''}
                        </span>
                      ) : (
                        '—'
                      )}
                    </div>
                    <div className="ctable-cell-muted" style={{ flex: 1 }}>
                      {recipient.unsubscribedAt ? (
                        <span style={{ color: 'var(--c-orange)' }}>
                          {isSms ? 'Opted out' : 'Unsubscribed'} {messageTime(recipient.unsubscribedAt)}
                        </span>
                      ) : recipient.repliedAt ? (
                        // The display id is the conversation number agents already
                        // use, so "#1234" can be looked up from the inbox search.
                        <span style={{ color: 'var(--c-green)' }}>
                          Replied {messageTime(recipient.repliedAt)}
                          {recipient.repliedConversationDisplayId
                            ? ` · conversation #${recipient.repliedConversationDisplayId}`
                            : ''}
                        </span>
                      ) : (
                        (recipient.error ?? '')
                      )}
                    </div>
                  </div>
                );
              })}
            {!recipientsLoad.stale && recipients.length === 0 && (
              <div className="ctable-empty">No recipients match this filter.</div>
            )}
          </div>

          {hasMoreRecipients && !recipientsLoad.stale && (
            <>
              {/* Reaching the last row IS the request for the next page. The button
                  stays for the times the observer cannot fire, and says what is
                  happening while a page is in flight. */}
              <div ref={moreSentinel} aria-hidden />
              <button
                type="button"
                className="cbtn"
                style={{ alignSelf: 'center' }}
                disabled={appended.loading}
                onClick={loadMoreRecipients}
              >
                {appended.loading && <span className="btn-spin" />}
                {appended.loading ? 'Loading…' : 'Load more recipients'}
              </button>
            </>
          )}
        </>
      )}

      {/* The warning the operator asked for, and it is a warning rather than a
          question: the mail already delivered cannot be recalled, which is the one
          thing somebody hitting the brake in a hurry needs told. */}
      {confirmStop && (
        <ConfirmDialog
          title="Stop this campaign?"
          confirmLabel="Stop sending"
          danger
          busy={controlBusy === 'stop'}
          body={
            <>
              <p>
                Sending stops within a few seconds. The{' '}
                <b>{stats.sent.toLocaleString()} already mailed cannot be recalled</b> — this only
                holds back the {stats.pending.toLocaleString()} still waiting.
              </p>
              <p>
                Nothing is lost: the campaign keeps its funnel, you can fix the subject or the body
                while it sits there, and <b>Resume sending</b> carries on from where it stopped.
              </p>
            </>
          }
          onCancel={() => setConfirmStop(false)}
          onConfirm={() =>
            runControl(
              'stop',
              () => source.api.stopCampaign(campaign.id),
              'Could not stop the campaign.',
            )
          }
        />
      )}

      {resendOpen && (
        <CampaignFollowUpPanel
          campaign={campaign}
          picked={[...picked]}
          initialSegment={resendSegment}
          onClose={() => setResendOpen(false)}
          onCreated={(draft) => {
            setResendOpen(false);
            setPicked(new Set());
            onResendDraft(draft);
          }}
        />
      )}
    </div>
  );
}
